import { IDateTimeOptions } from '../models/date-time-format-options.model';

export const FALLBACK_LOCALE = 'en-US';
// Default fallback for no-tz/unauthenticated surfaces (MER-4956): America/Denver (MST)
// is a more sensible default than UTC for most users. Authenticated callers that hold the
// user's profile timezone pass it in as the preferredFallback so it wins over this default.
export const FALLBACK_TIMEZONE = 'America/Denver';

// Privacy browsers (and some crawlers) block timezone fingerprinting and report the
// non-IANA zone 'Etc/Unknown'. Passing it to Intl or date-fns-tz throws a RangeError.
// Exported so callers and tests reference one source of truth for the sentinel. The
// try/catch in isValidTimezone rejects it regardless; the explicit check below is a
// documented fast-path for this common privacy-browser value, not the only guard.
export const INVALID_TIMEZONE = 'Etc/Unknown';

/**
 * Returns true when the given value is a usable IANA timezone that Intl will accept.
 */
export function isValidTimezone(timezone: string | undefined | null): boolean {
  if (!timezone || timezone === INVALID_TIMEZONE) {
    return false;
  }

  try {
    // Intl.DateTimeFormat throws a RangeError for a non-IANA zone. Called without `new`
    // (still constructs and validates) to satisfy the no-new lint rule.
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}

/**
 * Normalizes a timezone to a safe IANA zone. Precedence when the primary zone is missing,
 * 'Etc/Unknown', or otherwise invalid: (1) preferredFallback if it is itself a valid zone
 * (e.g. an authenticated caller's user_profile.time_zone), else (2) FALLBACK_TIMEZONE
 * (America/Denver). The primary zone always wins when it is valid.
 */
export function normalizeTimezone(
  timezone: string | undefined | null,
  preferredFallback?: string | null
): string {
  if (isValidTimezone(timezone)) {
    return timezone as string;
  }
  if (isValidTimezone(preferredFallback)) {
    return preferredFallback as string;
  }
  return FALLBACK_TIMEZONE;
}

function getFormatOption(
  format: IDateTimeOptions | undefined,
  timezone: string
): Intl.DateTimeFormatOptions {
  // Define format options
  const formatOptions: Record<string, Intl.DateTimeFormatOptions> = {
    short: {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      timeZone: timezone
    },
    medium: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: timezone
    },
    legacyMedium: {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: true,
      timeZone: timezone
    },
    long: {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: timezone
    },
    full: {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: timezone
    },
    abbreviated: {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: timezone
    },
    shortTime: {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
      timeZone: timezone
    },
    shortTimeWithTZ: {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
      timeZone: timezone,
      timeZoneName: 'short'
    },
    mediumTime: {
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: true,
      timeZone: timezone
    },
    shortDateTime: {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
      timeZone: timezone
    },
    mediumDateTime: {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
      timeZone: timezone,
      timeZoneName: 'short'
    },
    longDateTime: {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
      timeZone: timezone,
      timeZoneName: 'short'
    },
    weekday: {
      weekday: 'short',
    }
  };

  return format
    ? formatOptions[format]
    : formatOptions['medium'];
}

export function formatDateWithOptions(
  dateObj: Date,
  format?: IDateTimeOptions,
  locale?: string,
  timezone?: string,
  customFormat?: Intl.DateTimeFormatOptions | undefined,
  preferredFallback?: string
): string {
  // Intl.DateTimeFormat with timeZone option handles timezone conversion automatically.
  // Normalize the zone first so an invalid/Etc-Unknown timezone cannot throw a RangeError.
  // preferredFallback (e.g. an authenticated user_profile.time_zone) wins over the
  // America/Denver default when the requested zone is invalid.
  const safeTimezone = normalizeTimezone(timezone, preferredFallback);
  const intlOptions = customFormat
    ? {
      ...customFormat,
      // Only guard an explicitly-provided zone; leave it unset so Intl keeps its default.
      ...customFormat.timeZone
        ? { timeZone: normalizeTimezone(customFormat.timeZone, preferredFallback) }
        : {}
    }
    : getFormatOption(format, safeTimezone);

  try {
    return new Intl.DateTimeFormat(locale, intlOptions).format(dateObj);
  } catch {
    // A bad locale is the only remaining failure mode; keep the (already safe) timeZone.
    return new Intl.DateTimeFormat(FALLBACK_LOCALE, intlOptions).format(dateObj);
  }
}
